// Component: RecoveryActionModal.jsx
// Purpose: Shared confirmation modal for the 104 Element R (D67/R12) self-service /
//          admin-backup recovery actions (force-close, repair-netting). ONE modal,
//          reused by both StuckOrdersPanel.jsx (user-facing, primary) and
//          AdminRecoveryPanel.jsx (admin backup) — the backend enforces the SAME
//          X-Acknowledge-Loss gate on both routes (R12.2's AC: the fence is a backend
//          boundary, never a UI-only restriction), so the frontend confirmation UX
//          should not fork either (feedback_reuse_discovery_before_patching_loopbacks).

import React, { useState } from 'react';
import { Modal, Button, Checkbox, Textarea, Input } from '../ds';

//* mode: 'force-close' | 'repair-netting' — only changes the copy + which extra
//* field (true_exit_price) is shown; the ack-gate + submit shape stay identical.
const RecoveryActionModal = ({
  open,
  onClose,
  onConfirm,
  mode = 'force-close',
  instrumentLabel,
  submitting = false,
  errorMessage = null,
}) => {
  /*>>> State <<<*/
  const [reason, setReason] = useState('');
  const [complianceNote, setComplianceNote] = useState('');
  const [truePrice, setTruePrice] = useState('');
  //! acknowledge_loss — the explicit self-service confirmation the backend REQUIRES
  //! (400 if false/missing). Never pre-checked.
  const [acknowledged, setAcknowledged] = useState(false);

  const isRepair = mode === 'repair-netting';
  const title = isRepair ? 'Repair netting (books-only)' : 'Force-close position';

  /*>>> Handlers <<<*/
  const handleClose = () => {
    setReason('');
    setComplianceNote('');
    setTruePrice('');
    setAcknowledged(false);
    onClose?.();
  };

  const handleConfirm = () => {
    if (!acknowledged || submitting) return;
    onConfirm?.({
      reason: reason.trim(),
      compliance_note: complianceNote.trim(),
      true_exit_price: isRepair && truePrice.trim() ? truePrice.trim() : undefined,
      acknowledge_loss: true,
    });
  };

  const canSubmit = acknowledged && complianceNote.trim().length > 0 && !submitting;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirm} disabled={!canSubmit}>
            {submitting ? 'Submitting…' : isRepair ? 'Repair netting' : 'Force-close now'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-fg-2">
          {isRepair
            ? `This corrects the books for ${instrumentLabel} — it does NOT place a broker order. Only use this if the broker account is already flat.`
            : `This dispatches a REAL order at ${instrumentLabel} to close the position on your broker account.`}
        </p>

        {!isRepair && (
          <label className="text-sm">
            Reason
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you force-closing this position?"
              rows={2}
            />
          </label>
        )}

        {isRepair && (
          <label className="text-sm">
            True broker exit price (optional)
            <Input
              value={truePrice}
              onChange={(e) => setTruePrice(e.target.value)}
              placeholder="e.g. 182.35"
              inputMode="decimal"
            />
          </label>
        )}

        <label className="text-sm">
          Compliance note (required, kept for the audit trail)
          <Textarea
            value={complianceNote}
            onChange={(e) => setComplianceNote(e.target.value)}
            placeholder="Note for the record — what happened and why"
            rows={2}
          />
        </label>

        {/*! acknowledge_loss — the explicit self-service confirmation gate. */}
        <Checkbox
          checked={acknowledged}
          onChange={setAcknowledged}
          label={
            isRepair
              ? "I confirm the broker account is already flat and I understand this changes my books."
              : "I understand this dispatches a real order on my broker account and may realize a loss."
          }
        />

        {errorMessage && (
          <p className="text-sm text-loss" role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
};

export default RecoveryActionModal;
